import React from 'react';
import type { TFunction } from 'i18next';
import type { Goal, Filters } from '../types';
import { Card, CardContent } from '../../../../components/ui/Card';
import { statusLabel } from '../utils';

interface GoalListViewProps {
  filteredGoals: Goal[];
  loading: boolean;
  filters: Filters;
  handleGoalClick: (goal: Goal) => void;
  t: TFunction;
}

// Clarity chip color: null → neutral, >=80 green, >=50 amber, else red.
const clarityColor = (score?: number | null): string => {
  if (score == null) return 'bg-muted text-muted-foreground';
  if (score >= 80) return 'bg-success/10 text-success';
  if (score >= 50) return 'bg-warning/10 text-warning';
  return 'bg-destructive/10 text-destructive';
};

const statusColors: Record<string, { bg: string; text: string }> = {
  'completed': { bg: 'bg-success/10', text: 'text-success' },
  'on-track': { bg: 'bg-primary/10', text: 'text-primary' },
  'at-risk': { bg: 'bg-warning/10', text: 'text-warning' },
  'overdue': { bg: 'bg-error/10', text: 'text-error' },
};

// Complete literal class strings: Tailwind only compiles classes it can see in the source.
const levelStyles: Record<Goal['level'], { border: string; hover: string }> = {
  company: {
    border: 'border-s-4 border-s-primary',
    hover: 'hover:shadow-[0_4px_12px_rgba(37,99,235,0.15)]',
  },
  team: {
    border: 'border-s-4 border-s-warning',
    hover: 'hover:shadow-[0_4px_12px_rgba(245,158,11,0.15)]',
  },
  individual: {
    border: 'border-2 border-border',
    hover: 'hover:shadow-[0_4px_12px_rgba(107,114,128,0.1)]',
  },
};

function GoalCard({
  goal,
  level,
  showDepartment,
  handleGoalClick,
  t,
}: {
  goal: Goal;
  level: Goal['level'];
  showDepartment: boolean;
  handleGoalClick: (goal: Goal) => void;
  t: TFunction;
}) {
  const colors = statusColors[goal.status] || statusColors['overdue'];
  const styles = levelStyles[level] || levelStyles.individual;

  return (
    <Card
      variant="interactive"
      data-testid={`list-goal-${goal.id}`}
      className={`${styles.border} ${styles.hover} hover:-translate-y-0.5 transition-all duration-300 ${goal.isArchived ? 'opacity-60' : ''}`}
      onClick={() => handleGoalClick(goal)}
    >
      <CardContent className="p-3">
        <div className="flex justify-between mb-1">
          <h2 className="text-base font-medium">
            {goal.title}
            {goal.isArchived && (
              <span className="text-xs text-muted-foreground ms-1">
                {t('(Archived)')}
              </span>
            )}
          </h2>
          <div className="flex items-center gap-1 shrink-0">
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${clarityColor(goal.clarityScore)}`}
              title={t('goals.ai.clarityTooltip')}
              data-testid={`goal-clarity-${goal.id}`}
            >
              {/* CO-915: labelled, so the clarity score cannot read as goal progress beside the status. */}
                  {t('Clarity {{score}}', { score: goal.clarityScore == null ? '-' : `${goal.clarityScore}%` })}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
              {statusLabel(t, goal.status)}
            </span>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-1 line-clamp-2">
          {goal.description.split('\n')[0]}
        </p>
        <div className="flex justify-between items-center">
          <span className="text-xs text-muted-foreground">
            {goal.owner}{showDepartment ? ` • ${goal.department}` : ''}
          </span>
          <span className="text-xs text-muted-foreground">
            {t('Due:', { nsSeparator: false })} {goal.dueDate}
          </span>
        </div>
        <div className="mt-1 bg-muted rounded-full h-2">
          <div
            className="bg-primary h-full rounded-full"
            style={{ width: `${goal.progress}%` }}
          />
        </div>
      </CardContent>
    </Card>
  );
}

interface GoalSectionProps {
  title: string;
  level: Goal['level'];
  goals: Goal[];
  gradientIcon: React.ReactNode;
  showDepartment: boolean;
  handleGoalClick: (goal: Goal) => void;
  t: TFunction;
}

function GoalSection({
  title,
  level,
  goals,
  gradientIcon,
  showDepartment,
  handleGoalClick,
  t,
}: GoalSectionProps) {
  if (goals.length === 0) return null;

  return (
    <div className={level === 'individual' ? 'mb-4' : 'mb-6'}>
      <h2 className="mb-2 flex items-center gap-1 text-lg font-semibold">
        {gradientIcon}
        <span>{title}</span>
      </h2>
      <div className="grid gap-2">
        {goals.map((goal) => (
          <GoalCard
            key={goal.id}
            goal={goal}
            level={level}
            showDepartment={showDepartment}
            handleGoalClick={handleGoalClick}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}

export default function GoalListView({
  filteredGoals,
  loading,
  filters,
  handleGoalClick,
  t,
}: GoalListViewProps) {
  const companyGoals = filteredGoals.filter(g => g.level === 'company');
  const teamGoals = filteredGoals.filter(g => g.level === 'team');
  const individualGoals = filteredGoals.filter(g => g.level === 'individual');

  return (
    <div className="flex-1 overflow-auto max-h-[75vh] p-3" data-testid="goal-list-view">
      <div className="max-w-screen-xl mx-auto">
        {loading ? (
          <div className="flex justify-center p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <>
            <GoalSection
              title={t('Company Goals')}
              level="company"
              goals={companyGoals}
              gradientIcon={
                <span className="inline-block w-4 h-4 rounded-sm" style={{ background: 'linear-gradient(135deg, #2563eb 0%, #8b5cf6 100%)' }} />
              }
              showDepartment={false}
              handleGoalClick={handleGoalClick}
              t={t}
            />

            <GoalSection
              title={t('Team Goals')}
              level="team"
              goals={teamGoals}
              gradientIcon={
                <span className="inline-block w-4 h-4 rounded-sm" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #eab308 100%)' }} />
              }
              showDepartment={true}
              handleGoalClick={handleGoalClick}
              t={t}
            />

            <GoalSection
              title={t('Individual Goals')}
              level="individual"
              goals={individualGoals}
              gradientIcon={
                <span className="inline-block w-4 h-4 rounded-sm border-2 border-muted-foreground" />
              }
              showDepartment={true}
              handleGoalClick={handleGoalClick}
              t={t}
            />

            {filteredGoals.length === 0 && !loading && (
              <Card variant="elevated">
                <CardContent className="p-4 text-center">
                  <p className="text-muted-foreground">
                    {t('No goals found.')} {filters.search ? t('Try a different search term.') : t('Create a new goal to get started.')}
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
